import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import BreadCrumb from 'admin/components/molecules/BreadCrumb';
import Headings from 'admin/components/atoms/Headings';
import { fetchOrganizations } from 'admin/actions/organization';
import { getPrivateMoneys } from 'admin/actions/private-moneys';
import Select from 'admin/components/atoms/Select';
import TextBox from 'admin/components/atoms/TextBox';
import TextArea from 'admin/components/atoms/TextArea';
import { Button } from 'admin/components/atoms/Buttons';
import Input from 'admin/components/shared/Input';
import Img from 'admin/components/atoms/Img';
import ConfirmModal from 'admin/components/atoms/ConfirmModal';
import ApiResults from 'admin/components/molecules/ApiResults';
import useOAuthClient from 'admin/hooks/useOAuthClient';
import { OAuthClientHeaders, Row, RowCell } from './OAuthClientHeaders';
import CheckBoxList from './CheckBoxList';
import OAuthClientConfirmModal from './OAuthClientConfirmModal';
import { useOAuthClientForm } from './useOAuthClientForm';
import {
  allowedGrantTypeOptions,
  defaultAllowedGrantTypes,
  defaultScopeOptions,
  defaultDefaultScopes,
  creatingAccountTypeOptions,
  concealUserExistenceOptions,
} from './constants';

const FormWrapper = styled.div`
  ${TextBox} {
    width: 500px;
  }
  ${Select} {
    width: 500px;
  }
  ${TextArea} {
    width: 500px;
    resize: both;
  }
`;

const CreateOAuthClient = () => {
  const crumbs = [
    { value: 'ホーム', href: '/', isCurrentPage: false },
    { value: 'サービス管理', href: '', isCurrentPage: true },
    { value: 'OAuthClient', href: '/service-settings/oauth-clients', isCurrentPage: false },
    { value: 'OAuthClient登録', href: '', isCurrentPage: true },
  ];

  const [defaultMoneyOptions, setDefaultMoneyOptions] = useState([{ label: '指定しない', value: null }]);

  const { oauthClient, submitCreate } = useOAuthClient();
  // 登録前だけ許可種別の既定値を入れる。作成後は id の付いたサーバ応答をそのまま表示させたいので、
  // ここで上書きすると実際に作られた値と画面がずれる
  const initForm = useMemo(
    () =>
      oauthClient.id
        ? oauthClient
        : {
          ...oauthClient,
          allowed_grant_types: defaultAllowedGrantTypes,
          default_scopes: defaultDefaultScopes,
        },
    [oauthClient]
  );
  const { form, setForm, handleChangeForm, imgPreview } = useOAuthClientForm(initForm);

  const [organizationOptions, setOrganizationOptions] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [apiResult, setApiResult] = useState(null);

  const fetchPrivateMoneys = (org) => {
    if (org.value != null) {
      getPrivateMoneys(`organization_code=${org.value.code}&per_page=10000`).then((ret) => {
        const moneys = ret.rows.map(m => ({ label: m.name, value: m }));
        setDefaultMoneyOptions(moneys.concat({ label: '指定しない', value: null }));
      });
    }
  };

  const openModal = (e) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const onSuccess = () => {
    setApiResult({ text: 'OAuthClientは正常に作成されました', isError: false });
  };

  const registerOAuthClient = () => {
    let bodyParams = {
      name: form.name,
      redirect_uri: form.redirect_uri,
      organization_code: form.organization.value.code,
      creating_account_type: form.creating_account_type,
      description: form.description,
    };

    if (form.allowed_grant_types.length !== 0) {
      bodyParams = Object.assign({}, bodyParams, { allowed_grant_types: form.allowed_grant_types.join(' ') });
    }

    if (form.default_scopes.length !== 0) {
      bodyParams = Object.assign({}, bodyParams, { default_scopes: form.default_scopes.join(' ') });
    }

    if (form.default_private_money !== null) {
      bodyParams = Object.assign({}, bodyParams, { default_private_money_id: form.default_private_money.id });
    }

    if (form.oauth_client_image !== null) {
      bodyParams = Object.assign({}, bodyParams, { image: form.oauth_client_image });
    }

    if (form.conceal_user_existence !== null) {
      bodyParams = Object.assign({}, bodyParams, { conceal_user_existence: form.conceal_user_existence });
    }

    submitCreate(bodyParams, onSuccess);

    closeModal();
  };

  useEffect(() => {
    fetchOrganizations('per_page=10000').then((res) => {
      const orgs = res.rows.map(o => ({ label: o.name, value: o }));
      setOrganizationOptions(orgs);
    });
  }, []);

  useEffect(() => {
    if (form.organization !== null) {
      fetchPrivateMoneys(form.organization);
      setForm({ ...form, default_private_money: null });
    }
  }, [form.organization]);

  useEffect(() => {
    setForm({ ...form, default_private_money: null });
  }, [form.creating_account_type]);

  return (
    <FormWrapper>
      <BreadCrumb crumbs={crumbs} />
      <Headings.h5>OAuthClient登録</Headings.h5>
      <form onSubmit={openModal}>
        <Row>
          <OAuthClientHeaders keyName="organization" />
          <Select
            options={organizationOptions}
            value={form.organization}
            onChange={handleChangeForm('organization')}
            placeholder="組織を選択してください。"
            required
          />
        </Row>
        <Row>
          <OAuthClientHeaders keyName="name" />
          <RowCell>
            <TextBox
              value={form.name}
              onBlur={handleChangeForm('name', v => v.target.value)}
              required
            />
          </RowCell>
        </Row>
        <Row>
          <OAuthClientHeaders keyName="redirect_uri" />
          <RowCell>
            <TextBox
              value={form.redirect_uri}
              onBlur={handleChangeForm('redirect_uri', v => v.target.value)}
              required
            />
          </RowCell>
        </Row>
        <Row>
          <OAuthClientHeaders keyName="allowed_grant_types" />
          <RowCell>
            <CheckBoxList
              name="allowed-grant-types"
              options={allowedGrantTypeOptions}
              values={form.allowed_grant_types}
              onChange={handleChangeForm('allowed_grant_types')}
            />
          </RowCell>
        </Row>
        <Row>
          <OAuthClientHeaders keyName="default_scopes" />
          <RowCell>
            <CheckBoxList
              name="default-scopes"
              options={defaultScopeOptions}
              values={form.default_scopes}
              onChange={handleChangeForm('default_scopes')}
            />
          </RowCell>
        </Row>
        <Row>
          <OAuthClientHeaders keyName="creating_account_type" />
          <RowCell>
            <Select
              options={creatingAccountTypeOptions}
              value={
                creatingAccountTypeOptions.find(v => (
                  v.value === form.creating_account_type.toLowerCase()
                ))
              }
              onChange={handleChangeForm('creating_account_type', v => v.value)}
            />
          </RowCell>
        </Row>
        {form.creating_account_type.toLowerCase() === 'one'
         ? (
           <Row>
             <OAuthClientHeaders keyName="default_private_money" />
             <RowCell>
               <Select
                 Select
                 options={defaultMoneyOptions}
                 value={
                   form.default_private_money !== null
                     ? { label: form.default_private_money.name, value: form.default_private_money }
                   : { label: '指定しない', value: null }
                 }
                 onChange={handleChangeForm('default_private_money', v => v.value)}
               />
             </RowCell>
           </Row>
         )
         : null}
        <Row>
          <OAuthClientHeaders keyName="description" />
          <RowCell>
            <TextArea
              cols="90"
              value={form.description || ''}
              onChange={handleChangeForm('description', v => v.currentTarget.value)}
              maxLength={300}
              placeholder="OAuthClient説明文を記載"
            />
          </RowCell>
        </Row>
        <Row>
          <OAuthClientHeaders keyName="oauth_client_image" />
          <RowCell>
            <Img
              src={imgPreview}
              alt=""
              height="100px"
            />
            <Input
              type="file"
              onChange={handleChangeForm('oauth_client_image', v => v.target.files[0])}
              accept="image/jpeg, image/png"
            />
          </RowCell>
        </Row>
        <Row>
          <OAuthClientHeaders keyName="conceal_user_existence" />
          <RowCell>
            <Select
              options={concealUserExistenceOptions}
              value={concealUserExistenceOptions.find(op => op.value === form.conceal_user_existence)}
              onChange={handleChangeForm('conceal_user_existence', v => v.value)}
            />
          </RowCell>
        </Row>
        <Button>登録</Button>
      </form>
      <ConfirmModal
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        title="登録内容の確認"
        form={(
          <OAuthClientConfirmModal
            form={form}
            onConfirm={registerOAuthClient}
            onCancel={closeModal}
            imgPreview={imgPreview}
          />
        )}
      />
      {apiResult ? <ApiResults messages={[apiResult]} /> : null}
    </FormWrapper>
  );
};

export default CreateOAuthClient;
